import {
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { useTranslation } from "react-i18next";

import { Eyebrow } from "../primitives/Eyebrow";
import { helpTextStyle } from "../primitives/helpTextStyle";
import type { UiTheme } from "../theme";
import { WidgetConfigRow } from "./WidgetConfigRow";
import type { useSortableDrag } from "./useSortableDrag";

// HR-11: one sortable list of a widget config sheet. Narrow has one (the whole
// surface); wide Home has two, main and rail, like the real layout. Each owns
// its drag, so a row never crosses columns: the catalog decides the column.
// The surface comes in as props (PG-57): names, hideability, and the
// collapsed state where the surface has one.
export function WidgetConfigColumn<Id extends string>({
  theme,
  title,
  pinned,
  movable,
  drag,
  labelOf,
  isHideable,
  onMoveTo,
  onHide,
  collapsed,
  onSetCollapsed,
  style,
}: {
  theme: UiTheme;
  /** Wide only: which column of the surface this list is. */
  title?: string;
  pinned: Id[];
  movable: Id[];
  drag: ReturnType<typeof useSortableDrag>;
  labelOf: (id: Id) => string;
  isHideable: (id: Id) => boolean;
  /** Move `id` to slot `slot` of `movable`. */
  onMoveTo: (id: Id, slot: number) => void;
  onHide: (id: Id) => void;
  /** Present only on a surface with a collapsed state (Progress). */
  collapsed?: readonly Id[];
  onSetCollapsed?: (id: Id, collapsed: boolean) => void;
  style?: StyleProp<ViewStyle>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const empty = pinned.length === 0 && movable.length === 0;
  return (
    <View style={style}>
      {title ? (
        <Eyebrow theme={theme} style={styles.title}>
          {title}
        </Eyebrow>
      ) : null}
      {empty ? (
        <Text style={styles.hint}>
          {t("mobile.home.config.columnEmpty", {
            defaultValue: "No cards in this column.",
          })}
        </Text>
      ) : null}
      {pinned.map((id) => (
        <WidgetConfigRow
          key={id}
          kind="pinned"
          theme={theme}
          label={labelOf(id)}
        />
      ))}
      {movable.map((id, i) => {
        const closed = collapsed?.includes(id) ?? false;
        return (
          <WidgetConfigRow
            key={id}
            kind="active"
            theme={theme}
            label={labelOf(id)}
            onHide={isHideable(id) ? () => onHide(id) : undefined}
            onMoveBy={(delta) => onMoveTo(id, i + delta)}
            canMoveUp={i > 0}
            canMoveDown={i < movable.length - 1}
            position={i}
            handle={drag.handleProps(i)}
            dragging={drag.draggingIndex === i}
            offset={drag.rowOffset(i)}
            onLayout={drag.onRowLayout}
            collapse={
              onSetCollapsed
                ? { collapsed: closed, onToggle: () => onSetCollapsed(id, !closed) }
                : undefined
            }
          />
        );
      })}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    title: { marginBottom: s.s2 },
    hint: { ...helpTextStyle(theme), marginBottom: s.s3 },
  });
}
